import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ApiError, useSkillsApi } from '../apis/skills-apis';
import { Skill } from '../types';
import { useToast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";
import { Card, CardContent } from "../components/ui/Card";
import SkillForm, { SkillFormRef } from './SkillForm';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { useNavigation } from '../contexts/NavigationContext';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_SKILL_TIMEOUT } from '../config/api-config';

export const EditSkillPage: React.FC = () => {
  const formRef = useRef<SkillFormRef>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const skillsApi = useSkillsApi();
  const [editedSkill, setEditedSkill] = useState<Skill | null>(null);
  const [originalSkill, setOriginalSkill] = useState<Skill | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(() => {
    return !(location.state as { startEditing?: boolean })?.startEditing;
  });
  const initialFetchMade = useRef(false);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [existingSkillNames, setExistingSkillNames] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const backButtonClicked = useRef(false);
  const historyStateAdded = useRef(false);
  const { user } = useAuth();
  const { 
    showNavigationWarning, 
    pendingNavigation, 
    setShowNavigationWarning, 
    setPendingNavigation 
  } = useNavigation();
  
  const hasUnsavedChanges = !isReadOnly && 
    JSON.stringify(editedSkill) !== JSON.stringify(originalSkill);

  // Handle browser back button
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      // Only handle if there are unsaved changes
      if (hasUnsavedChanges) {
        backButtonClicked.current = true;
        setShowNavigationWarning(true);
        setPendingNavigation('/marketplace/skills');
      } else {
        // If no unsaved changes, allow normal back navigation
        navigate('/marketplace/skills');
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Add a single history state entry if not already added
    if (!historyStateAdded.current && hasUnsavedChanges) {
      window.history.pushState(null, '', window.location.pathname);
      historyStateAdded.current = true;
    }

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, navigate, setShowNavigationWarning, setPendingNavigation]);

  useEffect(() => {
    const fetchSkillAndNames = async () => {
      if (id && !initialFetchMade.current) {
        initialFetchMade.current = true;
        try {
          const getAllSkills = true;
          const skill = await skillsApi.getSkill(id);
          const allSkills = await skillsApi.getSkills(getAllSkills);

          setExistingSkillNames(
            allSkills
              .filter(s => s.skillId !== id)
              .map(s => s.skillName)
          );
          
          const allTags = new Set<string>();
          allSkills.forEach(skill => {
            skill.skillTags?.forEach(tag => {
              if (tag) allTags.add(tag);
            });
          });
          
          setTagSuggestions(Array.from(allTags));
  
          if (skill) {
            const updatedSkill = {
              ...skill,
              skillTags: skill.skillTags || [],
              skillTimeout: skill.skillTimeout || DEFAULT_SKILL_TIMEOUT
            };
            setEditedSkill(updatedSkill);
            setOriginalSkill(updatedSkill);
          } else {
            addToast({
              title: "Error",
              description: "Skill not found",
              variant: "destructive",
              duration: 4000
            });
            navigate('/marketplace/skills');
          }
        } catch (error: any) {
          console.error('Error fetching data:', error);
          
          if (error instanceof ApiError && error.status === 403) {
            navigate('/marketplace/unauthorized');
            return;
          }
          
          // For all other errors, show the specific error message or fallback
          const errorMessage = error instanceof ApiError 
            ? error.message 
            : "Failed to fetch skill details";
            
          addToast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
            duration: 5000
          });
          navigate('/marketplace/skills');
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchSkillAndNames();
  }, [id, addToast, navigate, skillsApi]);

  const handleSubmit = async () => {
    if (!editedSkill) {
      addToast({
        title: "Error",
        description: "No skill data available to save",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
  
    const isValid = await formRef.current?.validateForm();
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
  
    setIsSaving(true);
  
    try {
      const skillToUpdate = {
        ...editedSkill,
        skillName: editedSkill.skillName.trim(),
        skillDescription: editedSkill.skillDescription.trim(),
        skillUrlEndpoint: editedSkill.skillUrlEndpoint.trim(),
        skillTimeout: editedSkill.skillTimeout || DEFAULT_SKILL_TIMEOUT,
        skillParameters: {
          parameters: editedSkill.skillParameters?.parameters || [],
          testMode: editedSkill.skillParameters?.testMode ?? true
        },
        skillOutput: editedSkill.skillOutput || { parameters: {} },
        skillAnnotation: editedSkill.skillAnnotation || '',
        skillTags: editedSkill.skillTags || [],  // Ensure tags are initialized
        isCore: editedSkill.isCore ?? false // This is currently not set in the UI
      };
  
      if (editedSkill.skillAuthentication && editedSkill.skillAuthentication !== '••••••••') {
        skillToUpdate.skillAuthentication = editedSkill.skillAuthentication.trim();
      }
  
      const updatedSkill = await skillsApi.updateSkill(skillToUpdate);
      
      // Ensure tags are initialized in the response
      const normalizedSkill = {
        ...updatedSkill,
        skillTags: updatedSkill.skillTags || []
      };
      
      setOriginalSkill(normalizedSkill);
      setEditedSkill(normalizedSkill);
      setIsReadOnly(true);
      
      addToast({
        title: "Success",
        description: `Skill ${skillToUpdate.skillName} has been successfully updated`,
        variant: "default",
        duration: 4000
      });
  
      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      } else {
        navigate(`/marketplace/skills/test/${skillToUpdate.skillId}`);
      }
    } catch (error) {
      console.error('Error updating skill:', error);
      addToast({
        title: "Error",
        description: "Failed to update skill. Please try again.",
        variant: "destructive",
        duration: 4000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation(null);
    } else {
      setEditedSkill(originalSkill);
      setIsReadOnly(true);
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    setEditedSkill(originalSkill);
    setIsReadOnly(true);
    
    if (backButtonClicked.current) {
      backButtonClicked.current = false;
      historyStateAdded.current = false;
      navigate('/marketplace/skills');
    } else if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
    backButtonClicked.current = false;
    
    // If this was a back button navigation, restore the history state
    if (backButtonClicked.current) {
      window.history.pushState(null, '', window.location.pathname);
    }
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/marketplace/skills');
    } else {
      navigate('/marketplace/skills');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-blue-500" />
        <p className="text-center text-muted-foreground">Loading Skill...</p>
      </div>
    );
  }

  if (!editedSkill) {
    return (
      <Card className="max-w-2xl mx-auto m-4">
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">Skill not found.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-has-unsaved-changes={hasUnsavedChanges}>
      <SkillForm 
        ref={formRef}
        skill={editedSkill}
        originalSkill={originalSkill}
        onSkillChange={setEditedSkill}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={true}
        readOnly={isReadOnly}
        onEditClick={() => setIsReadOnly(false)}
        onDiscardChanges={handleDiscardChanges}
        hasUnsavedChanges={hasUnsavedChanges}
        setIsReadOnly={setIsReadOnly}
        tagSuggestions={tagSuggestions}
        existingSkillNames={existingSkillNames}
        currentUserId={user?.sub || ''}
        isSaving={isSaving}
      />
  
      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </div>
  );
};

export default EditSkillPage;